import { useEffect, useMemo, useState } from 'react';
import type { ITool, IToolContext } from '../../../core/tools/interfaces/ITool';
import type { ToolRegistry, ToolUiLocation } from '../../../core/tools/ToolRegistry';

export type ChromeMount = Extract<ToolUiLocation, 'selectionAction' | 'canvasCreate' | 'canvasMode'>;

export interface ChromeToolsResult {
  tools: ITool[];
  context: IToolContext | null;
}

/**
 * Filtered, registry-ordered tool set for a canvas chrome mount.
 *
 * The registry context is the single source of truth (tools-and-commands
 * architecture); this hook only subscribes and re-queries — it never scans
 * stores or owns ordering. ChromeMountPoint is the sole intended consumer.
 */
export function useChromeTools(
  toolRegistry: ToolRegistry | undefined,
  mount: ChromeMount,
): ChromeToolsResult {
  const [registryVersion, setRegistryVersion] = useState(0);

  useEffect(() => {
    if (!toolRegistry) return;
    const eventBus = toolRegistry.getContext().eventBus;
    const bump = () => setRegistryVersion((version) => version + 1);
    const offContextChanged = eventBus.on('tool:contextChanged', bump);
    // Domain plugins register tools asynchronously; a selection can exist
    // before registration completes (same reasoning as FloatingProperties).
    const offRegistered = eventBus.on('tool:registered', bump);
    const offUnregistered = eventBus.on('tool:unregistered', bump);
    // Toggle verbs (getActiveState) reflect command-driven state that can
    // change without a context bump — e.g. a highlight rule added/removed.
    const offExecuted = eventBus.on('command:executed', bump);
    const offUndone = eventBus.on('command:undone', bump);
    const offRedone = eventBus.on('command:redone', bump);
    bump();
    return () => {
      offContextChanged?.();
      offRegistered?.();
      offUnregistered?.();
      offExecuted?.();
      offUndone?.();
      offRedone?.();
    };
  }, [toolRegistry]);

  return useMemo<ChromeToolsResult>(() => {
    if (!toolRegistry) return { tools: [], context: null };
    const context = toolRegistry.getContext();
    const tools = toolRegistry
      .getToolsForLocation(mount, context.selectionSummary)
      .filter((tool) => tool.canExecute(context));
    return { tools, context };
    // registryVersion is the reactivity key: context changes, registrations,
    // and unregistrations all bump it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolRegistry, mount, registryVersion]);
}
